// Shared helpers used by ProjectsPage + ARMonitoringView + WOMonitoringView.
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

// Sortable table header (inline, no external dep). Cycles asc → desc → default (no sort).
function SortH({ label, k, sort, setSort, className = "" }) {
  const active = sort.key === k;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  const toggle = () => {
    if (!active) setSort({ key: k, dir: "asc" });
    else if (sort.dir === "asc") setSort({ key: k, dir: "desc" });
    else setSort({ key: null, dir: "asc" });
  };
  return (
    <th className={`px-2 py-2 overline text-slate-600 whitespace-nowrap ${className}`}>
      <button type="button" data-testid={`sort-${k}`} onClick={toggle}
        className={`inline-flex items-center gap-1 uppercase tracking-wider text-[10px] font-semibold hover:text-[#0B1D3A] ${active ? "text-[#0B1D3A]" : "text-slate-600"}`}>
        {label}
        <Icon size={10} className={active ? "text-[#FF5A1F]" : "text-slate-400"} />
      </button>
    </th>
  );
}

// Retensi (2026-02-15 session 149-ae — Combined VO drive formula):
//   • Combined VO + received: amount × pct / (100−pct)  ← gross-up (invoice = 90% bruto)
//   • Combined VO + waiting/no-received: 0
//   • Separate VO + outside: 0 (di-claim terpisah)
//   • Separate VO + inside : amount × pct / 100
// Retention-claim entries dikecualikan (nilai claim ≠ nilai retensi ditahan).
function computeEntryRetention(entry, project, voAggregation) {
  if (!entry || entry.is_retention_claim) return 0;
  const pct = entry.retention_pct != null ? Number(entry.retention_pct) : (Number(project?.retention_pct) || 0);
  if (pct <= 0 || pct >= 100) return 0;
  const amount = Number(entry.amount) || 0;
  const agg = voAggregation !== undefined ? voAggregation : (project?.vo_aggregation || "separate");
  if (agg === "combined") {
    // Combined mode: gross-up regardless of retention_type. Only for received invoices.
    const hasReceived = !!entry.date && !entry.waiting_payment;
    if (!hasReceived) return 0;
    return Math.round(amount * pct / (100 - pct));
  }
  // Separate VO: outside=0, inside=r/100
  const rt = entry.retention_type || project?.retention_type || "inside";
  if (rt === "outside") return 0;
  return Math.round(amount * pct / 100);
}
function computeRetentionHeld(project, voAggregation) {
  if (!project) return 0;
  const agg = voAggregation !== undefined ? voAggregation : (project.vo_aggregation || "separate");
  return (project.income_entries || []).reduce((s, ie) => s + computeEntryRetention(ie, project, agg), 0);
}

function computeRetentionClaimed(project) {
  if (!project) return 0;
  return (project.income_entries || []).reduce(
    (s, ie) => s + (ie.is_retention_claim ? (Number(ie.amount) || 0) : 0), 0
  );
}

// Tokenized multi-kata AND search matcher — dipakai di search box Projects / AR / WO
// agar UX konsisten. Contoh: "goodworth jakarta" match project di Jakarta dgn client Goodworth.
function tokenize(q) {
  const t = (q || "").trim().toLowerCase();
  return t ? t.split(/\s+/).filter(Boolean) : [];
}
function matchTokens(text, tokens) {
  if (!tokens.length) return true;
  const t = (text || "").toLowerCase();
  return tokens.every((tok) => t.includes(tok));
}

export { SortH, computeEntryRetention, computeRetentionHeld, computeRetentionClaimed, tokenize, matchTokens };
